import { Link } from 'gatsby'
import { useCallback } from 'react'
import { Image } from 'src/components/ui/Image'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

type Props = {
  name: string
  linkUrl: string
  image: string
  position?: number
  index: number
  section: string
  locationOnPage: string
}

export function CardBanner({
  image,
  linkUrl,
  name,
  position = 1,
  index,
  section,
  locationOnPage,
}: Props) {
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)

  const handleClickBanner = useCallback(() => {
    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: name ?? '',
      index,
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
      },
    })

    if (window === null) {
      return
    }

    window.dataLayer = window?.dataLayer ?? []

    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'promotionClick',
      ecommerce: {
        promoClick: {
          promotions: [
            {
              id: name,
              name,
              creative: image,
              position: `slot${position}`,
            },
          ],
        },
      },
    })
  }, [image, index, locationOnPage, name, position, section, standardObject])

  return (
    <Link
      to={linkUrl}
      className="overflow-hidden relative flex justify-between items-center rounded-md  w-full bg-restructure-background-secondary px-lg py-[22px] restructure-small-desktop:py-xl restructure-small-desktop:flex-col restructure-small-desktop:h-[238px]  restructure-small-desktop:items-start"
      onClick={() => handleClickBanner()}
    >
      <Image
        src={image}
        alt={name}
        width={280}
        height={238}
        sizes="(min-width: 1024px) 280px, 1px"
        className="object-cover absolute bottom-0 right-0 rounded-md w-full h-full hidden restructure-small-desktop:block transition-transform duration-500 ease-in-out hover:scale-110"
      />

      <h3 className="relative z-10 desktop-body-semibold-text1 text-restructure-secondary restructure-small-desktop:max-w-[140px]">
        {name}
      </h3>

      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        data-testid="arrowCard"
        className="relative z-10"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.6665 16C5.6665 15.4477 6.11422 15 6.6665 15H25.3332C25.8855 15 26.3332 15.4477 26.3332 16C26.3332 16.5523 25.8855 17 25.3332 17H6.6665C6.11422 17 5.6665 16.5523 5.6665 16Z"
          fill="#3E4751"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.2929 5.9594C15.6834 5.56887 16.3166 5.56887 16.7071 5.9594L26.0404 15.2927C26.431 15.6833 26.431 16.3164 26.0404 16.7069L16.7071 26.0403C16.3166 26.4308 15.6834 26.4308 15.2929 26.0403C14.9024 25.6498 14.9024 25.0166 15.2929 24.6261L23.9191 15.9998L15.2929 7.37361C14.9024 6.98309 14.9024 6.34992 15.2929 5.9594Z"
          fill="#3E4751"
        />
      </svg>
    </Link>
  )
}
